/**
 * CR-7420: who gets the bulk (multi-select) delivery-appeal controls on the
 * Deliveries grid — the checkbox column, the select-all header and the
 * "Appeal Selected (n)" bar.
 *
 * Extracted from DeliveryTab so the gate can be tested on its own; it decided
 * the visibility of three separate blocks and had no coverage at all.
 */

/**
 * Whether bulk-appeal multi-select is available to the current viewer.
 *
 * Both sides get it. The bulk endpoint has accepted customer-raised appeals
 * since CR-6313 — it classifies the side itself and auto-accepts the
 * customer's — and the platform toggle is described to admins as governing
 * suppliers and customers. The UI previously shipped only the supplier half.
 *
 * `hideAppealForSupplier` (the `hide_otif_override_button` Open Orders
 * control) hides the appeal controls from SUPPLIER users only, so it must not
 * gate the customer side. DeliveryRow's customer Acknowledge + Appeal branch
 * documents the identical carve-out.
 *
 * Pass the supplier VIEW (`useIsSupplier()`), not `user.platform`. The server
 * classifies the appeal's side with `appeal_actor_is_customer()`, which falls
 * back to `profile.portal_mode` for anyone whose company is not literally the
 * PO's customer or supplier — so the view is what the outcome will match.
 *
 * **Both arguments must come from the same signal.** `hideAppealForSupplierView`
 * has to be resolved with the view too, because `isOpenOrdersControlHidden`
 * short-circuits to `false` whenever its own `isSupplier` argument is false. Feed
 * it the platform while keying this gate on the view and a dual-portal ('both')
 * company acting as supplier gets a control the admin's toggle cannot suppress:
 * their company is never typed 'supplier', so the platform-keyed value is
 * permanently false.
 *
 * Known gap: a Helix admin who has pinned a supplier site still reads as a
 * customer view here, because the site selector is not part of the portal
 * mode. They would be offered the controls with the customer's wording.
 *
 * @param enabled - the `delivery_appeal_bulk_enabled` platform toggle
 * @param isSupplierView - the viewer is currently acting as a supplier
 * @param hideAppealForSupplierView - the hide-appeal control, resolved with the view
 * @returns true when the multi-select controls should render
 */
export function isBulkAppealModeActive(
  enabled: boolean,
  isSupplierView: boolean,
  hideAppealForSupplierView: boolean,
): boolean {
  return enabled && (!isSupplierView || !hideAppealForSupplierView);
}
